import 'reactflow/dist/style.css';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import MinimizeIcon from '@mui/icons-material/Minimize';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  MiniMap,
  Panel,
  Position,
} from 'reactflow';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// Status chip colors and labels
const STATUS_CONFIG = {
  expired: {color: 'default', label: 'Expired', bgColor: '#f5f5f5'},
  active: {color: 'success', label: 'Active', bgColor: '#e8f5e9'},
  future: {color: 'info', label: 'Future', bgColor: '#e3f2fd'},
  unknown: {color: 'warning', label: 'Unknown', bgColor: '#fff8e1'},
};

const StatusChip = ({status}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return (
    <Chip
      label={config.label}
      size="small"
      color={config.color}
      variant="outlined"
      sx={{mr: 0.5}}
    />
  );
};

StatusChip.propTypes = {
  status: PropTypes.oneOf(['expired', 'active', 'future', 'unknown']),
};

// Copy to clipboard component with visual feedback
const CopyToClipboard = ({text, size = 'small'}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    async e => {
      e.stopPropagation(); // Prevent node selection when clicking copy button
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500); // Reset after 1.5 seconds
      } catch (err) {
        // Silently handle copy failures
      }
    },
    [text]
  );

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${text}`} arrow>
      <IconButton
        onClick={handleCopy}
        size={size}
        sx={{
          padding: 0.25,
          minWidth: 'auto',
          ml: 0.5,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {copied ? (
          <CheckIcon sx={{fontSize: 14, color: 'success.main'}} />
        ) : (
          <ContentCopyIcon sx={{fontSize: 14, color: 'text.secondary'}} />
        )}
      </IconButton>
    </Tooltip>
  );
};

CopyToClipboard.propTypes = {
  text: PropTypes.string.isRequired,
  size: PropTypes.string,
};

const OrphanIndicator = ({tooltip}) => (
  <Tooltip title={tooltip} arrow>
    <WarningIcon
      sx={{
        color: '#f57c00',
        fontSize: 16,
        ml: 0.5,
        verticalAlign: 'middle',
      }}
    />
  </Tooltip>
);

OrphanIndicator.propTypes = {
  tooltip: PropTypes.string,
};

// Status sort order: expired first, then active, then future, then unknown
const STATUS_SORT_ORDER = {expired: 0, active: 1, future: 2, unknown: 3};

const sortByStatus = items => {
  return [...items].sort((a, b) => {
    const aOrder = STATUS_SORT_ORDER[a.status] ?? 3;
    const bOrder = STATUS_SORT_ORDER[b.status] ?? 3;
    if (aOrder !== bOrder) return aOrder - bOrder;
    // Secondary sort by start date
    const aDate = a.startDate || a.activeFrom || '';
    const bDate = b.startDate || b.activeFrom || '';
    return aDate.localeCompare(bDate);
  });
};

// Helper function to format TOA date with fallback
const formatTOADate = (date, fallback) => {
  return date || fallback;
};

// Helper to determine if TOA should be shown
const shouldShowTOA = data => {
  const {startDate, endDate, toaStartDate, toaEndDate} = data;

  // Don't show if both TOA dates are missing
  if (!toaStartDate && !toaEndDate) return false;

  // Don't show if TOA dates match term dates exactly
  if (toaStartDate === startDate && toaEndDate === endDate) return false;

  return true;
};

// Custom node components
const SalesforceProductNode = ({data, selected}) => {
  const isFiltered = data.isFiltered ?? false;

  return (
    <Box
      sx={{
        minWidth: selected ? 350 : 250,
        transition: 'all 0.3s ease',
        opacity: isFiltered ? 0.3 : 1,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <Card
        sx={{
          border: selected ? '3px solid #1976d2' : '2px solid #1976d2',
          bgcolor: data.status === 'expired' ? '#f5f5f5' : '#e3f2fd',
          boxShadow: selected ? 4 : 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#1976d2',
                overflow: selected ? 'visible' : 'hidden',
                textOverflow: selected ? 'initial' : 'ellipsis',
                whiteSpace: selected ? 'normal' : 'nowrap',
                transition: 'all 0.2s ease',
                flex: 1,
              }}
              title={data.name}
            >
              {selected
                ? data.name
                : data.name?.length > 20
                  ? `${data.name.substring(0, 20)}...`
                  : data.name}
            </Typography>
            {data.isOrphan && (
              <OrphanIndicator tooltip="No matching subscription found" />
            )}
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5}}>
            <StatusChip status={data.status} />
            {data.linkConfidence !== null &&
              data.linkConfidence !== undefined && (
                <Chip
                  label={`${Math.round(data.linkConfidence * 100)}%`}
                  size="small"
                  variant="outlined"
                  sx={{fontSize: '0.7rem'}}
                />
              )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Term: {data.startDate} to {data.endDate}
          </Typography>
          {shouldShowTOA(data) && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Data: {formatTOADate(data.toaStartDate, 'Start of term')} to{' '}
              {formatTOADate(data.toaEndDate, 'End of term')}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Ref: {data.plNumber}
          </Typography>
          {data.quantity && (
            <Chip
              label={`${data.quantity} ${data.quotaUnits || 'units'}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{mt: 1}}
            />
          )}
          {selected && data.opportunityName && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{mt: 1}}
            >
              Opportunity: {data.opportunityName}
            </Typography>
          )}
          {selected && data.edpId && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              EDP ID: {data.edpId}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

SalesforceProductNode.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    toaStartDate: PropTypes.string,
    toaEndDate: PropTypes.string,
    plNumber: PropTypes.string,
    quantity: PropTypes.number,
    quotaUnits: PropTypes.string,
    opportunityName: PropTypes.string,
    edpId: PropTypes.string,
    isFiltered: PropTypes.bool,
    status: PropTypes.oneOf(['expired', 'active', 'future', 'unknown']),
    isOrphan: PropTypes.bool,
    linkConfidence: PropTypes.number,
  }),
  selected: PropTypes.bool,
};

const ProductAccessNode = ({data, selected}) => {
  const isFiltered = data.isFiltered ?? false;

  return (
    <Box
      sx={{
        minWidth: selected ? 350 : 250,
        transition: 'all 0.3s ease',
        opacity: isFiltered ? 0.3 : 1,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <Card
        sx={{
          border: selected ? '3px solid #388e3c' : '2px solid #388e3c',
          bgcolor: '#e8f5e8',
          boxShadow: selected ? 4 : 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#388e3c',
              overflow: selected ? 'visible' : 'hidden',
              textOverflow: selected ? 'initial' : 'ellipsis',
              whiteSpace: selected ? 'normal' : 'nowrap',
              transition: 'all 0.2s ease',
            }}
            title={data.name} // Show full name on hover
          >
            {selected
              ? data.name
              : data.name?.length > 20
                ? `${data.name.substring(0, 20)}...`
                : data.name}
          </Typography>
          {data.uniqueId && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {data.uniqueId}
              </Typography>
              <CopyToClipboard text={data.uniqueId} />
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Term: {data.startDate} to {data.endDate}
          </Typography>
          {shouldShowTOA(data) && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Data: {formatTOADate(data.toaStartDate, 'Start of term')} to{' '}
              {formatTOADate(data.toaEndDate, 'End of term')}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block">
            Org ID: {data.organizationId}
          </Typography>
          {data.quotaLimit && !data.unlimitedQuota && (
            <Chip
              label={`${data.quotaLimit} ${data.quotaUnits || 'units'}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{mt: 1}}
            />
          )}
          {data.unlimitedQuota && (
            <Chip
              label="Unlimited"
              size="small"
              color="success"
              variant="outlined"
              sx={{mt: 1}}
            />
          )}
          {selected && data.quotaSqkm && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{mt: 1}}
            >
              Quota: {data.quotaSqkm} sq km
            </Typography>
          )}
          {selected && data.edpId && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              EDP ID: {data.edpId}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

ProductAccessNode.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    toaStartDate: PropTypes.string,
    toaEndDate: PropTypes.string,
    organizationId: PropTypes.number,
    quotaLimit: PropTypes.number,
    unlimitedQuota: PropTypes.bool,
    quotaUnits: PropTypes.string,
    quotaSqkm: PropTypes.number,
    edpId: PropTypes.string,
    uniqueId: PropTypes.string,
    isFiltered: PropTypes.bool,
  }),
  selected: PropTypes.bool,
};

const ExistingProductAccessNode = ({data, selected}) => {
  const isFiltered = data.isFiltered ?? false;

  return (
    <Box
      sx={{
        minWidth: selected ? 350 : 250,
        transition: 'all 0.3s ease',
        opacity: isFiltered ? 0.3 : 1,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Card
        sx={{
          border: selected ? '3px solid #d32f2f' : '2px solid #d32f2f',
          bgcolor: '#ffebee',
          boxShadow: selected ? 4 : 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#d32f2f',
              overflow: selected ? 'visible' : 'hidden',
              textOverflow: selected ? 'initial' : 'ellipsis',
              whiteSpace: selected ? 'normal' : 'nowrap',
              transition: 'all 0.2s ease',
            }}
            title={data.name}
          >
            {selected
              ? data.name
              : data.name?.length > 20
                ? `${data.name.substring(0, 20)}...`
                : data.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Org ID: {data.organizationId}
          </Typography>

          {/* Display individual PATs grouped within the PA */}
          {data.terms && data.terms.length > 0 ? (
            <Box sx={{mt: 1.5, mb: 1}}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                Terms ({data.terms.length}):
              </Typography>
              {data.terms.map((term, index) => (
                <Box
                  key={term.id}
                  sx={{
                    p: 1,
                    mb: index < data.terms.length - 1 ? 1 : 0,
                    bgcolor: 'rgba(211, 47, 47, 0.05)',
                    borderRadius: 1,
                    border: '1px solid rgba(211, 47, 47, 0.2)',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {term.startDate} to {term.endDate}
                  </Typography>
                  {term.quotaLimit && (
                    <Chip
                      label={`${term.quotaLimit} ${term.quotaUnits || 'units'}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{mt: 0.5}}
                    />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Term: {data.startDate} to {data.endDate}
              </Typography>
              {shouldShowTOA(data) && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Data: {formatTOADate(data.toaStartDate, 'Start of term')} to{' '}
                  {formatTOADate(data.toaEndDate, 'End of term')}
                </Typography>
              )}
              {data.quotaLimit && !data.unlimitedQuota && (
                <Chip
                  label={`${data.quotaLimit} ${data.quotaUnits || 'units'}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{mt: 1}}
                />
              )}
              {data.unlimitedQuota && (
                <Chip
                  label="Unlimited"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{mt: 1}}
                />
              )}
            </>
          )}
          {selected && data.quotaSqkm && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{mt: 1}}
            >
              Quota: {data.quotaSqkm} sq km
            </Typography>
          )}
          {selected && data.edpId && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              EDP ID: {data.edpId}
            </Typography>
          )}
          {selected && data.uniqueId && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Unique ID: {data.uniqueId}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

ExistingProductAccessNode.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    toaStartDate: PropTypes.string,
    toaEndDate: PropTypes.string,
    organizationId: PropTypes.number,
    quotaLimit: PropTypes.number,
    unlimitedQuota: PropTypes.bool,
    quotaUnits: PropTypes.string,
    quotaSqkm: PropTypes.number,
    edpId: PropTypes.string,
    uniqueId: PropTypes.string,
    isFiltered: PropTypes.bool,
    terms: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        quotaLimit: PropTypes.number,
        quotaUnits: PropTypes.string,
        status: PropTypes.string,
      })
    ),
  }),
  selected: PropTypes.bool,
};

const PlanPackageNode = ({data, selected}) => {
  const isFiltered = data.isFiltered ?? false;

  return (
    <Box
      sx={{
        minWidth: selected ? 350 : 250,
        transition: 'all 0.3s ease',
        opacity: isFiltered ? 0.3 : 1,
      }}
    >
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <Card
        sx={{
          border: selected ? '3px solid #f57c00' : '2px solid #f57c00',
          bgcolor: data.status === 'expired' ? '#f5f5f5' : '#fff8e1',
          boxShadow: selected ? 4 : 2,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{p: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#f57c00',
                overflow: selected ? 'visible' : 'hidden',
                textOverflow: selected ? 'initial' : 'ellipsis',
                whiteSpace: selected ? 'normal' : 'nowrap',
                transition: 'all 0.2s ease',
                flex: 1,
              }}
              title={data.planName}
            >
              {selected
                ? data.planName
                : data.planName?.length > 20
                  ? `${data.planName.substring(0, 20)}...`
                  : data.planName}
            </Typography>
            {data.isOrphan && (
              <OrphanIndicator tooltip="No matching line item found" />
            )}
          </Box>
          {data.subscriptionId && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: 'text.primary',
                fontWeight: 500,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {data.subscriptionId}
              </Typography>
              <CopyToClipboard text={data.subscriptionId.toString()} />
            </Box>
          )}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5}}>
            <StatusChip status={data.status} />
            {data.linkConfidence !== null &&
              data.linkConfidence !== undefined && (
                <Chip
                  label={`${Math.round(data.linkConfidence * 100)}%`}
                  size="small"
                  variant="outlined"
                  sx={{fontSize: '0.7rem'}}
                />
              )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Term: {data.activeFrom} to {data.activeTo}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Ref: {data.subscriptionReference}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Org ID: {data.organizationId}
          </Typography>
          <Box sx={{mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap'}}>
            {data.quotaSqkm && (
              <Chip
                label={`${data.quotaSqkm} sq km`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {data.quotaStyle && (
              <Chip
                label={data.quotaStyle}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          {selected && (
            <Box sx={{mt: 1}}>
              {data.planId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Plan ID: {data.planId}
                </Typography>
              )}
              {data.useBillableId && (
                <Chip
                  label="Billable"
                  size="small"
                  color="success"
                  variant="filled"
                  sx={{mt: 0.5}}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

PlanPackageNode.propTypes = {
  data: PropTypes.shape({
    planName: PropTypes.string,
    activeFrom: PropTypes.string,
    activeTo: PropTypes.string,
    subscriptionReference: PropTypes.string,
    organizationId: PropTypes.number,
    quotaSqkm: PropTypes.number,
    quotaStyle: PropTypes.string,
    subscriptionName: PropTypes.string,
    planId: PropTypes.number,
    useBillableId: PropTypes.bool,
    isFiltered: PropTypes.bool,
    status: PropTypes.oneOf(['expired', 'active', 'future', 'unknown']),
    isOrphan: PropTypes.bool,
    linkConfidence: PropTypes.number,
    subscriptionId: PropTypes.number,
    linkedLineItemId: PropTypes.number,
  }),
  selected: PropTypes.bool,
};

const nodeTypes = {
  salesforceProduct: SalesforceProductNode,
  productAccess: ProductAccessNode,
  planPackage: PlanPackageNode,
  existingProductAccess: ExistingProductAccessNode,
};

const MigrationTreeView = ({
  migrationData,
  organizationIds, // eslint-disable-line no-unused-vars
  currentPlanId, // eslint-disable-line no-unused-vars
  onPlanModified, // eslint-disable-line no-unused-vars
  onAddLinkModification,
  onRemoveLinkModification,
  onAddProductAccessModification,
  hasModifications, // eslint-disable-line no-unused-vars
}) => {
  const [orgFilters, setOrgFilters] = useState({});
  const [showInactive, setShowInactive] = useState(true);
  const [refFilters, setRefFilters] = useState({});
  const [showEmptyRefs, setShowEmptyRefs] = useState(true);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState([]);

  // Modification UI state
  const [isLinkingMode, setIsLinkingMode] = useState(false);
  const [sourcePlanNode, setSourcePlanNode] = useState(null);
  const [editingQuota, setEditingQuota] = useState(null); // { nodeId, originalValue, currentValue, units }
  const [quotaEditValue, setQuotaEditValue] = useState('');

  // Selection toolbar state
  const [selectedNodeType, setSelectedNodeType] = useState(null); // 'plan' | 'productAccess'
  const [selectedNodeData, setSelectedNodeData] = useState(null);

  const formatDate = useCallback(dateString => {
    if (!dateString) return 'N/A';
    return dateString.split('T')[0];
  }, []);

  // Helper function to split ref values on delimiters
  const splitRefValue = useCallback(refValue => {
    if (!refValue) return [];
    return refValue
      .split(/[,;\s]+/)
      .map(val => val.trim())
      .filter(val => val.length > 0);
  }, []);

  // Extract unique organization IDs from migration data
  const availableOrgIds = useMemo(() => {
    if (!migrationData?.results?.migration_plan) return [];

    const context = migrationData.results.migration_plan.context || {};
    const orgIds = new Set();

    // Get org IDs from product accesses
    context.product_accesses?.forEach(pa => {
      if (pa.organization_id) orgIds.add(pa.organization_id);
    });

    // Get org IDs from legacy plans
    context.legacy_plans?.forEach(plan => {
      if (plan.organization_id) orgIds.add(plan.organization_id);
    });

    return Array.from(orgIds).sort();
  }, [migrationData]);

  // Extract unique ref values from migration data
  const availableRefs = useMemo(() => {
    if (!migrationData?.results?.migration_plan) return [];

    const context = migrationData.results.migration_plan.context || {};
    const refs = new Set();

    // Get refs from Salesforce products
    context.salesforce_products?.forEach(sfProduct => {
      if (sfProduct.pl_number) {
        splitRefValue(sfProduct.pl_number).forEach(ref => refs.add(ref));
      }
    });

    // Get refs from legacy plans
    context.legacy_plans?.forEach(plan => {
      if (plan.subscription_reference) {
        splitRefValue(plan.subscription_reference).forEach(ref =>
          refs.add(ref)
        );
      }
    });

    return Array.from(refs).sort();
  }, [migrationData, splitRefValue]);

  // Initialize org filters when org IDs change
  useMemo(() => {
    const newFilters = {};
    availableOrgIds.forEach(orgId => {
      newFilters[orgId] = true; // Show all orgs by default
    });
    setOrgFilters(newFilters);
  }, [availableOrgIds]);

  // Initialize ref filters when refs change
  useMemo(() => {
    const newFilters = {};
    availableRefs.forEach(ref => {
      newFilters[ref] = true; // Show all refs by default
    });
    setRefFilters(newFilters);
  }, [availableRefs]);

  // Helper function to check if a date indicates inactive status
  const isItemActive = useCallback((startDate, endDate) => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    // Consider active if current date is within the range
    const isInDateRange = (!start || start <= now) && (!end || end >= now);
    return isInDateRange;
  }, []);

  const toggleOrgFilter = useCallback(orgId => {
    setOrgFilters(prev => ({
      ...prev,
      [orgId]: !prev[orgId],
    }));
  }, []);

  const toggleRefFilter = useCallback(ref => {
    setRefFilters(prev => ({
      ...prev,
      [ref]: !prev[ref],
    }));
  }, []);

  const toggleSelectAll = useCallback(() => {
    const allSelected =
      showInactive &&
      showEmptyRefs &&
      availableOrgIds.every(orgId => orgFilters[orgId]) &&
      availableRefs.every(ref => refFilters[ref]);
    const newState = !allSelected;

    // Toggle all filters
    setShowInactive(newState);
    setShowEmptyRefs(newState);

    const newOrgFilters = {};
    availableOrgIds.forEach(orgId => {
      newOrgFilters[orgId] = newState;
    });
    setOrgFilters(newOrgFilters);

    const newRefFilters = {};
    availableRefs.forEach(ref => {
      newRefFilters[ref] = newState;
    });
    setRefFilters(newRefFilters);
  }, [
    availableOrgIds,
    orgFilters,
    availableRefs,
    refFilters,
    showInactive,
    showEmptyRefs,
  ]);

  // Helper function to check if a card should be greyed out based on ref filters
  const shouldGreyOutByRef = useCallback(
    refValue => {
      if (!refValue) {
        return !showEmptyRefs; // Grey out if "Show empty refs" is off
      }

      const cardRefs = splitRefValue(refValue);
      if (cardRefs.length === 0) {
        return !showEmptyRefs; // Grey out if "Show empty refs" is off
      }

      // Grey out if any of the card's refs are filtered out
      return cardRefs.some(ref => !refFilters[ref]);
    },
    [refFilters, showEmptyRefs, splitRefValue]
  );

  const onNodesChange = useCallback(changes => {
    // Handle node selection changes
    const selectionChanges = changes.filter(change => change.type === 'select');
    if (selectionChanges.length > 0) {
      const newSelectedNodes = selectionChanges
        .filter(change => change.selected)
        .map(change => change.id);
      setSelectedNodes(newSelectedNodes);
    }
  }, []);

  // Handle node selection for modification UI
  const handleNodeClick = useCallback(
    (event, node) => {
      if (isLinkingMode && sourcePlanNode) {
        // In linking mode, clicking a Product Access node creates a link
        if (node.type === 'productAccess') {
          onAddLinkModification(
            sourcePlanNode.subscriptionId,
            node.data.lineItemId
          );
          setIsLinkingMode(false);
          setSourcePlanNode(null);
          event.stopPropagation();
          return;
        }
      }

      // Regular node selection for toolbar display
      if (node.type === 'planPackage') {
        setSelectedNodeType('plan');
        setSelectedNodeData(node.data);
        setSelectedNodes([node.id]); // Update ReactFlow selection state
      } else if (node.type === 'productAccess') {
        setSelectedNodeType('productAccess');
        setSelectedNodeData(node.data);
        setSelectedNodes([node.id]); // Update ReactFlow selection state
      }
    },
    [isLinkingMode, sourcePlanNode, onAddLinkModification]
  );

  // Link modification actions
  const handleEditLink = useCallback(() => {
    if (!selectedNodeData) return;
    setIsLinkingMode(true);
    setSourcePlanNode(selectedNodeData);
    setSelectedNodeType(null);
    setSelectedNodeData(null);
  }, [selectedNodeData]);

  const handleRemoveLink = useCallback(() => {
    if (!selectedNodeData) return;
    onRemoveLinkModification(selectedNodeData.subscriptionId);
    setSelectedNodeType(null);
    setSelectedNodeData(null);
  }, [selectedNodeData, onRemoveLinkModification]);

  // Quota modification actions
  const handleEditQuota = useCallback(() => {
    if (!selectedNodeData) return;
    setEditingQuota({
      nodeId: selectedNodeData.id,
      uniqueId: selectedNodeData.uniqueId,
      originalValue: selectedNodeData.quotaLimit || 0,
      currentValue: selectedNodeData.quotaLimit || 0,
      units: selectedNodeData.quotaUnits || 'sqkm',
    });
    setQuotaEditValue(String(selectedNodeData.quotaLimit || 0));
    setSelectedNodeType(null);
    setSelectedNodeData(null);
  }, [selectedNodeData]);

  const handleSaveQuota = useCallback(() => {
    if (!editingQuota) return;

    const newValue = parseInt(quotaEditValue, 10);
    if (isNaN(newValue) || newValue < 0) return;

    // Find the unique_id from the editing quota data instead of searching nodes
    // The editingQuota should contain the node data we need
    if (editingQuota.uniqueId) {
      onAddProductAccessModification(editingQuota.uniqueId, {
        quota_limit: newValue,
        quota_units: editingQuota.units,
      });
    }

    setEditingQuota(null);
    setQuotaEditValue('');
  }, [editingQuota, quotaEditValue, onAddProductAccessModification]);

  const handleResetQuota = useCallback(() => {
    if (!editingQuota) return;
    setQuotaEditValue(String(editingQuota.originalValue));
  }, [editingQuota]);

  const handleCancelQuota = useCallback(() => {
    setEditingQuota(null);
    setQuotaEditValue('');
  }, []);

  const handleGrandfatherQuota = useCallback(() => {
    if (!editingQuota) return;

    // Use the unique_id from the editing quota data instead of searching nodes
    if (editingQuota.uniqueId) {
      onAddProductAccessModification(editingQuota.uniqueId, {
        quota_limit: null, // null means unlimited
        unlimited_quota: true,
      });
    }

    setEditingQuota(null);
    setQuotaEditValue('');
  }, [editingQuota, onAddProductAccessModification]);

  const onPaneClick = useCallback(() => {
    setSelectedNodes([]);
    setSelectedNodeType(null);
    setSelectedNodeData(null);
    setIsLinkingMode(false);
    setSourcePlanNode(null);
  }, []);

  const {nodes, edges} = useMemo(() => {
    if (!migrationData?.results) {
      return {nodes: [], edges: []};
    }

    const results = migrationData.results;

    if (results.status !== 'success' || !results.migration_plan) {
      return {nodes: [], edges: []};
    }

    const migrationPlan = results.migration_plan;
    const context = migrationPlan?.context || {};
    const overlay = migrationPlan?.overlay || {};
    const salesforceProducts = [];
    const plansPackages = [];
    const productAccesses = [];
    const existingProductAccesses = [];

    // Build line item link lookup for orphan detection and confidence
    const lineItemLinksByLineItemId = {};
    const lineItemLinksBySubscriptionId = {};
    overlay.proposed_line_item_links?.forEach(link => {
      // Index by line_item_id
      if (!lineItemLinksByLineItemId[link.line_item_id]) {
        lineItemLinksByLineItemId[link.line_item_id] = [];
      }
      lineItemLinksByLineItemId[link.line_item_id].push(link);

      // Index by subscription_id
      if (!lineItemLinksBySubscriptionId[link.subscription_id]) {
        lineItemLinksBySubscriptionId[link.subscription_id] = [];
      }
      lineItemLinksBySubscriptionId[link.subscription_id].push(link);
    });

    // Extract Salesforce Products (Line Items)
    context.salesforce_products?.forEach(sfProduct => {
      const links = lineItemLinksByLineItemId[sfProduct.line_item_id] || [];
      const bestLink = links.reduce(
        (best, link) =>
          !best || link.link_confidence > best.link_confidence ? link : best,
        null
      );

      salesforceProducts.push({
        id: `sf-${sfProduct.line_item_id}`,
        lineItemId: sfProduct.line_item_id,
        name: sfProduct.product_name || 'Unknown Product',
        edpId: sfProduct.edp_id,
        quantity: sfProduct.quantity,
        quotaUnits: sfProduct.unit_of_measure,
        plNumber: sfProduct.pl_number,
        opportunityName: sfProduct.opportunity_name,
        startDate: formatDate(sfProduct.start_date),
        endDate: formatDate(sfProduct.end_date),
        toaStartDate: formatDate(sfProduct.toa_start_date),
        toaEndDate: formatDate(sfProduct.toa_end_date),
        refValue: sfProduct.pl_number,
        status: sfProduct.status || 'unknown',
        isOrphan: links.length === 0,
        linkConfidence: bestLink?.link_confidence,
      });
    });

    // Extract Product Accesses
    context.product_accesses?.forEach(pa => {
      const isActive = isItemActive(pa.start_date, pa.end_date);
      const shouldShow = orgFilters[pa.organization_id] ?? true;

      if (shouldShow) {
        // Find linked Salesforce product to get ref value
        const linkedSfProduct = salesforceProducts.find(
          sf => sf.lineItemId === pa.line_item_id
        );

        productAccesses.push({
          id: `pa-${pa.unique_id}`,
          uniqueId: pa.unique_id,
          lineItemId: pa.line_item_id,
          name: pa.edp_id || 'Product Access',
          edpId: pa.edp_id,
          quotaLimit: pa.quota_limit,
          quotaUnits: pa.quota_units,
          quotaSqkm: pa.quota_sqkm,
          unlimitedQuota: pa.unlimited_quota,
          startDate: formatDate(pa.start_date),
          endDate: formatDate(pa.end_date),
          toaStartDate: formatDate(pa.toa_start_date),
          toaEndDate: formatDate(pa.toa_end_date),
          organizationId: pa.organization_id,
          isActive,
          refValue: linkedSfProduct?.refValue || null,
          existingIds: pa.existing_ids || [],
          status: pa.status || 'unknown',
        });
      }
    });

    // Extract Existing Product Accesses
    context.existing_product_accesses?.forEach(epa => {
      const isActive = isItemActive(epa.start_date, epa.end_date);
      const shouldShow = orgFilters[epa.organization_id] ?? true;

      if (shouldShow) {
        existingProductAccesses.push({
          id: `epa-${epa.unique_id}`,
          uniqueId: epa.unique_id,
          name: epa.edp_id || 'Existing Product Access',
          edpId: epa.edp_id,
          quotaLimit: epa.quota_limit,
          quotaUnits: epa.quota_units,
          quotaSqkm: epa.quota_sqkm,
          unlimitedQuota: epa.unlimited_quota,
          startDate: formatDate(epa.start_date),
          endDate: formatDate(epa.end_date),
          toaStartDate: formatDate(epa.toa_start_date),
          toaEndDate: formatDate(epa.toa_end_date),
          organizationId: epa.organization_id,
          isActive,
          refValue: null, // Existing PAs don't have refs for filtering
          terms:
            epa.terms?.map(term => ({
              id: term.id,
              startDate: formatDate(term.start_date),
              endDate: formatDate(term.end_date),
              quotaLimit: term.quota_limit,
              quotaUnits: term.quota_units,
              status: term.status,
            })) || [],
        });
      }
    });

    // Extract Plans & Packages (Subscriptions)
    context.legacy_plans?.forEach(plan => {
      const isActive = isItemActive(plan.active_from, plan.active_to);
      const shouldShow = orgFilters[plan.organization_id] ?? true;

      if (shouldShow) {
        const usageLink = overlay.proposed_usage_links?.find(
          link => link.subscription_id === plan.subscription_id
        );
        const useBillableId =
          usageLink?.product_access_usage?.[0]?.use_billable_id || false;

        // Check for line item links (Sub ↔ LI relationship)
        const lineItemLinks =
          lineItemLinksBySubscriptionId[plan.subscription_id] || [];
        const bestLineItemLink = lineItemLinks.reduce(
          (best, link) =>
            !best || link.link_confidence > best.link_confidence ? link : best,
          null
        );

        plansPackages.push({
          id: `plan-${plan.plan_id}-${plan.subscription_id}`,
          planId: plan.plan_id,
          planName: plan.plan_name,
          subscriptionId: plan.subscription_id,
          subscriptionName: plan.subscription_name,
          subscriptionReference: plan.subscription_reference,
          quotaStyle: plan.quota_style,
          quotaSqkm: plan.quota_sqkm,
          activeFrom: formatDate(plan.active_from),
          activeTo: formatDate(plan.active_to),
          organizationId: plan.organization_id,
          useBillableId: useBillableId,
          linkConfidence: bestLineItemLink?.link_confidence,
          linkedLineItemId: bestLineItemLink?.line_item_id,
          isActive,
          refValue: plan.subscription_reference,
          status: plan.status || 'unknown',
          isOrphan: lineItemLinks.length === 0,
          lineItemLinks: lineItemLinks, // Store for edge creation
        });
      }
    });

    // Create nodes
    const nodeList = [];
    const edgeList = [];

    // Sort all arrays by status before creating nodes
    const sortedSalesforceProducts = sortByStatus(salesforceProducts);
    const sortedProductAccesses = sortByStatus(productAccesses);
    const sortedPlansPackages = sortByStatus(plansPackages);
    const sortedExistingProductAccesses = sortByStatus(existingProductAccesses);

    // Create Salesforce Product (Line Item) nodes
    sortedSalesforceProducts.forEach((product, index) => {
      const greyedByRef = shouldGreyOutByRef(product.refValue);
      const greyedByStatus = !showInactive && product.status === 'expired';

      nodeList.push({
        id: product.id,
        type: 'salesforceProduct',
        position: {x: 0, y: index * 200},
        data: product,
        selected: selectedNodes.includes(product.id),
        style: {
          opacity: greyedByRef || greyedByStatus ? 0.5 : 1,
        },
      });
    });

    // Create Product Access nodes
    sortedProductAccesses.forEach((pa, index) => {
      const greyedByActivity = !showInactive && !pa.isActive;
      const greyedByRef = shouldGreyOutByRef(pa.refValue);
      const greyedByStatus = !showInactive && pa.status === 'expired';

      nodeList.push({
        id: pa.id,
        type: 'productAccess',
        position: {x: 400, y: index * 200},
        data: pa,
        selected: selectedNodes.includes(pa.id),
        style: {
          opacity: greyedByActivity || greyedByRef || greyedByStatus ? 0.5 : 1,
        },
      });
    });

    // Create Plan Package (Subscription) nodes
    sortedPlansPackages.forEach((plan, index) => {
      const greyedByActivity = !showInactive && !plan.isActive;
      const greyedByRef = shouldGreyOutByRef(plan.refValue);
      const greyedByStatus = !showInactive && plan.status === 'expired';

      nodeList.push({
        id: plan.id,
        type: 'planPackage',
        position: {x: 800, y: index * 200}, // Adjusted position for better layout
        data: {
          ...plan,
        },
        selected: selectedNodes.includes(plan.id),
        style: {
          opacity: greyedByActivity || greyedByRef || greyedByStatus ? 0.5 : 1,
        },
      });
    });

    // Create Existing Product Access nodes
    sortedExistingProductAccesses.forEach((epa, index) => {
      const greyedByActivity = !showInactive && !epa.isActive;
      const greyedByStatus = !showInactive && epa.status === 'expired';

      nodeList.push({
        id: epa.id,
        type: 'existingProductAccess',
        position: {x: 1200, y: index * 200}, // Position to the right of subscriptions
        data: epa,
        selected: selectedNodes.includes(epa.id),
        style: {
          opacity: greyedByActivity || greyedByStatus ? 0.5 : 1,
        },
      });
    });

    // Create edges from SF Products (Line Items) to Product Accesses
    salesforceProducts.forEach(sfProduct => {
      const matchingPAs = productAccesses.filter(
        pa => pa.lineItemId === sfProduct.lineItemId
      );
      matchingPAs.forEach(pa => {
        edgeList.push({
          id: `sf-pa-${sfProduct.id}-${pa.id}`,
          source: sfProduct.id,
          target: pa.id,
          animated: true,
          style: {
            stroke: '#1976d2',
            strokeWidth: 2,
          },
        });
      });
    });

    // Create edges from Subscriptions to Line Items (using proposed_line_item_links)
    plansPackages.forEach(plan => {
      if (plan.lineItemLinks && plan.lineItemLinks.length > 0) {
        plan.lineItemLinks.forEach(link => {
          const matchingSfProduct = salesforceProducts.find(
            sf => sf.lineItemId === link.line_item_id
          );

          if (matchingSfProduct) {
            // Determine edge style based on confidence
            const isHighConfidence = link.link_confidence >= 0.6;
            edgeList.push({
              id: `plan-sf-${plan.id}-${matchingSfProduct.id}`,
              source: plan.id,
              target: matchingSfProduct.id,
              animated: isHighConfidence,
              label: `${Math.round(link.link_confidence * 100)}%`,
              labelStyle: {fontSize: 10, fill: '#666'},
              labelBgStyle: {fill: '#fff', fillOpacity: 0.8},
              style: {
                stroke: isHighConfidence ? '#f57c00' : '#999999',
                strokeWidth: isHighConfidence ? 2 : 1,
                strokeDasharray: isHighConfidence ? 'none' : '5,5',
              },
            });
          }
        });
      }
    });

    // Create edges from Product Accesses to Plans (via usage links)
    overlay.proposed_usage_links?.forEach(link => {
      const matchingPlan = plansPackages.find(
        plan => plan.subscriptionId === link.subscription_id
      );

      if (matchingPlan) {
        link.product_access_usage?.forEach(pau => {
          const matchingPA = productAccesses.find(
            pa => pa.uniqueId === pau.product_access_unique_id
          );

          if (matchingPA) {
            edgeList.push({
              id: `pa-plan-${matchingPA.id}-${matchingPlan.id}`,
              source: matchingPA.id,
              target: matchingPlan.id,
              animated: pau.use_billable_id,
              style: {
                stroke: pau.use_billable_id ? '#1976d2' : '#666666',
                strokeWidth: 2,
                strokeDasharray: pau.use_billable_id ? 'none' : '5,5',
              },
            });
          }
        });
      }
    });

    // Create edges from Product Accesses to Existing Product Accesses
    productAccesses.forEach(pa => {
      if (pa.existingIds && pa.existingIds.length > 0) {
        pa.existingIds.forEach(existingId => {
          const matchingEPA = existingProductAccesses.find(
            epa => epa.uniqueId === existingId
          );

          if (matchingEPA) {
            edgeList.push({
              id: `pa-epa-${pa.id}-${matchingEPA.id}`,
              source: pa.id,
              target: matchingEPA.id,
              animated: false,
              style: {
                stroke: '#d32f2f',
                strokeWidth: 2,
                strokeDasharray: '3,3',
              },
            });
          }
        });
      }
    });

    return {nodes: nodeList, edges: edgeList};
  }, [
    migrationData,
    formatDate,
    orgFilters,
    showInactive,
    isItemActive,
    shouldGreyOutByRef,
    selectedNodes,
  ]);

  if (!migrationData?.results) {
    return null;
  }

  const results = migrationData.results;

  if (results.status !== 'success' || !results.migration_plan) {
    return (
      <Typography variant="body2" color="error">
        {results.status === 'error'
          ? results.error_message
          : 'No migration plan found.'}
      </Typography>
    );
  }

  if (!nodes || nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No migration data found to display.
      </Typography>
    );
  }

  return (
    <Box sx={{height: '80vh', width: '100%'}}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        proOptions={{hideAttribution: true}}
        onNodesChange={onNodesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={onPaneClick}
      >
        <Background variant="dots" gap={12} size={1} />
        <Controls />
        <MiniMap />

        {/* Selection-based Toolbars */}
        {selectedNodeType === 'plan' && (
          <Panel position="top-left">
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '2px solid #f57c00',
                borderRadius: 2,
                p: 2,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                display: 'flex',
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleEditLink}
                sx={{fontSize: '0.75rem'}}
              >
                Edit Link
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleRemoveLink}
                sx={{fontSize: '0.75rem'}}
              >
                Remove Links
              </Button>
            </Box>
          </Panel>
        )}

        {selectedNodeType === 'productAccess' && (
          <Panel position="top-left">
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '2px solid #388e3c',
                borderRadius: 2,
                p: 2,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                display: 'flex',
                gap: 1,
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={handleEditQuota}
                sx={{fontSize: '0.75rem'}}
              >
                Edit Quota
              </Button>
            </Box>
          </Panel>
        )}

        {isLinkingMode && (
          <Panel position="bottom-center">
            <Box
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.95)',
                color: 'white',
                border: '2px solid #1976d2',
                borderRadius: 2,
                p: 2,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{fontWeight: 500}}>
                Click a Product Access node to create a link
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setIsLinkingMode(false);
                  setSourcePlanNode(null);
                }}
                sx={{
                  mt: 1,
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Panel>
        )}

        {/* Filter Toggle/Panel */}
        <Panel position="top-right">
          {!showFilterPanel ? (
            /* Collapsed Filter Icon */
            <IconButton
              onClick={() => setShowFilterPanel(true)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ddd',
                borderRadius: 1,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <FilterListIcon fontSize="small" />
            </IconButton>
          ) : (
            /* Expanded Filter Panel */
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ddd',
                borderRadius: 2,
                p: 2,
                minWidth: 200,
                maxWidth: 250,
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                position: 'relative',
              }}
            >
              {/* Minimize Button */}
              <IconButton
                onClick={() => setShowFilterPanel(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  width: 24,
                  height: 24,
                  padding: 0,
                }}
              >
                <MinimizeIcon fontSize="small" />
              </IconButton>

              {/* Select All / Deselect All */}
              <Box sx={{mb: 1, pr: 3}}>
                <Button
                  size="small"
                  variant="text"
                  onClick={toggleSelectAll}
                  sx={{
                    minWidth: 'auto',
                    p: 0,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    color: 'primary.main',
                    fontWeight: 500,
                  }}
                >
                  {showInactive &&
                  showEmptyRefs &&
                  availableOrgIds.every(orgId => orgFilters[orgId]) &&
                  availableRefs.every(ref => refFilters[ref])
                    ? 'Deselect all'
                    : 'Select all'}
                </Button>
              </Box>

              {/* Show Inactive Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={e => setShowInactive(e.target.checked)}
                    size="small"
                  />
                }
                label="Show inactive"
                sx={{
                  mb: 0.5,
                  display: 'block',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  },
                }}
              />

              {/* Show Empty Refs Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={showEmptyRefs}
                    onChange={e => setShowEmptyRefs(e.target.checked)}
                    size="small"
                  />
                }
                label="Show empty refs"
                sx={{
                  mb: 0.5,
                  display: 'block',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                  },
                }}
              />

              {/* Organization Filters */}
              {availableOrgIds.length > 0 && (
                <Box sx={{maxHeight: 150, overflowY: 'auto'}}>
                  {availableOrgIds.map(orgId => (
                    <FormControlLabel
                      key={orgId}
                      control={
                        <Switch
                          checked={orgFilters[orgId] ?? true}
                          onChange={() => toggleOrgFilter(orgId)}
                          size="small"
                        />
                      }
                      label={`Org ${orgId}`}
                      sx={{
                        mb: 0.5,
                        display: 'block',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Ref Filters */}
              {availableRefs.length > 0 && (
                <Box sx={{maxHeight: 150, overflowY: 'auto'}}>
                  {availableRefs.map(ref => (
                    <FormControlLabel
                      key={ref}
                      control={
                        <Switch
                          checked={refFilters[ref] ?? true}
                          onChange={() => toggleRefFilter(ref)}
                          size="small"
                        />
                      }
                      label={`Ref ${ref}`}
                      sx={{
                        mb: 0.5,
                        display: 'block',
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.875rem',
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Panel>
      </ReactFlow>

      {/* Quota Edit Modal */}
      {editingQuota && (
        <Dialog
          open={Boolean(editingQuota)}
          onClose={handleCancelQuota}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Product Access Quota</DialogTitle>
          <DialogContent>
            <Box sx={{pt: 2}}>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Original quota: {editingQuota.originalValue}{' '}
                {editingQuota.units}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Quota Limit"
                type="number"
                fullWidth
                variant="outlined"
                value={quotaEditValue}
                onChange={e => setQuotaEditValue(e.target.value)}
                inputProps={{min: 0}}
                helperText={`Units: ${editingQuota.units}`}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{gap: 1}}>
            <Button onClick={handleResetQuota} size="small">
              Reset
            </Button>
            <Button
              onClick={handleGrandfatherQuota}
              size="small"
              color="success"
            >
              Grandfather
            </Button>
            <Button onClick={handleCancelQuota} size="small">
              Cancel
            </Button>
            <Button
              onClick={handleSaveQuota}
              variant="contained"
              size="small"
              disabled={
                !quotaEditValue ||
                isNaN(parseInt(quotaEditValue, 10)) ||
                parseInt(quotaEditValue, 10) < 0
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

MigrationTreeView.propTypes = {
  migrationData: PropTypes.object,
  organizationIds: PropTypes.arrayOf(PropTypes.number),
  currentPlanId: PropTypes.string,
  onPlanModified: PropTypes.func,
  onAddLinkModification: PropTypes.func,
  onRemoveLinkModification: PropTypes.func,
  onAddProductAccessModification: PropTypes.func,
  hasModifications: PropTypes.bool,
};

export default MigrationTreeView;
